import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, LogIn } from 'lucide-react';
import { DataFlowWordmark } from './DataFlowWordmark';

export interface SiteHeaderProps {
  onOpenDemo: () => void;
  onOpenDiagnosis: () => void;
}

export function SiteHeader({ onOpenDemo, onOpenDiagnosis }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const navLinks = [
    { label: 'Módulos', href: '#solutions' },
    { label: 'Infraestrutura', href: '#platform' },
    { label: 'Autoridade', href: '#score' },
    { label: 'Segurança', href: '#security' },
    { label: 'Contato', href: '#contact' },
  ];

  const scrollTo = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <header
      className={`lp-site-header fixed top-0 left-0 right-0 z-50 border-b border-slate-200 bg-white transition-shadow duration-300 ${
        scrolled ? 'shadow-sm' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[62px]">
          <a
            href="#inicio"
            onClick={(e) => { e.preventDefault(); scrollTo('#inicio'); }}
            className="flex items-center select-none"
            aria-label="DataFlow — início"
          >
            <DataFlowWordmark />
          </a>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Seções">
            {navLinks.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => scrollTo(link.href)}
                className="text-slate-600 hover:text-slate-900 transition-colors"
                style={{ fontSize: '0.875rem', fontWeight: 500 }}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2.5">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 transition-all px-4 py-2 rounded-md"
              style={{ fontSize: '0.855rem', fontWeight: 500 }}
            >
              <LogIn size={14} strokeWidth={2} />
              Entrar
            </Link>
            <button
              type="button"
              onClick={onOpenDiagnosis}
              className="text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 transition-all px-4 py-2 rounded-md"
              style={{ fontSize: '0.855rem', fontWeight: 500 }}
            >
              Solicitar Diagnóstico
            </button>
            <button
              type="button"
              onClick={onOpenDemo}
              className="text-white bg-blue-600 hover:bg-blue-700 transition-colors px-4 py-2 rounded-md shadow-sm"
              style={{ fontSize: '0.855rem', fontWeight: 600 }}
            >
              Agendar Demo
            </button>
          </div>

          <button
            type="button"
            className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100">
          <div className="px-6 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => scrollTo(link.href)}
                className="text-left text-slate-700 hover:text-blue-600 py-2 text-sm transition-colors"
                style={{ fontWeight: 500 }}
              >
                {link.label}
              </button>
            ))}
            <div className="pt-3 border-t border-slate-100 flex flex-col gap-2 mt-1">
              <Link
                to="/login"
                className="text-center text-slate-700 border border-slate-200 rounded-md py-2.5 text-sm"
                style={{ fontWeight: 500 }}
                onClick={() => setMobileOpen(false)}
              >
                Entrar na Plataforma
              </Link>
              <button
                type="button"
                onClick={() => { onOpenDiagnosis(); setMobileOpen(false); }}
                className="text-slate-700 border border-slate-200 rounded-md py-2.5 text-sm"
                style={{ fontWeight: 500 }}
              >
                Solicitar Diagnóstico Operacional
              </button>
              <button
                type="button"
                onClick={() => { onOpenDemo(); setMobileOpen(false); }}
                className="text-white bg-blue-600 rounded-md py-2.5 text-sm"
                style={{ fontWeight: 600 }}
              >
                Agendar Demonstração Técnica
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
